const REQUEST_TYPE = "sprymeeting:launch-access-token-request";
const RESPONSE_TYPE = "sprymeeting:launch-access-token-response";
const WINDOW_NAME_PREFIX = "__SPRYMEETING_LAUNCH_ACCESS_TOKEN__=";
const HANDLER_TTL_MS = 15_000;

export type MeetingLaunchErrorState = {
  title: string;
  message: string;
};

interface RegisterLaunchTokenResponderOptions {
  childWindow: Window;
  childOrigin: string;
  token: string;
}

export const parseMeetingLaunchError = (
  error: unknown,
  sourceLabel: string,
): MeetingLaunchErrorState => {
  const response =
    typeof error === "object" && error !== null && "response" in error
      ? (error as { response?: { status?: number; data?: { code?: string; message?: string } } }).response
      : undefined;
  const status = response?.status;
  const code = response?.data?.code || "";
  const backendMessage = response?.data?.message?.trim();

  switch (code) {
    case "document_rooms_disabled":
      return {
        title: "Document rooms are disabled for this workspace",
        message:
          "This workspace cannot start linked SpryMeeting sessions yet. Ask a workspace admin to enable document rooms and confirm the workspace is on a Pro plan.",
      };
    case "workspace_app_disabled":
      return {
        title: "SpryMeeting is disabled",
        message: "SpryMeeting is currently disabled for this workspace. Ask a workspace admin to enable the app and try again.",
      };
    case "workspace_app_blocked":
    case "workspace_role_forbidden":
      return {
        title: "You do not have access to start meetings",
        message: "Your current workspace role or app access settings do not allow linked SpryMeeting sessions from this board.",
      };
    case "workspace_membership_required":
    case "workspace_membership_inactive":
    case "workspace_membership_suspended":
    case "workspace_membership_blocked":
      return {
        title: "Workspace access is required",
        message: "Your account does not currently have active SpryMeeting access for this workspace. Re-open the board from SpryWorkspace or contact an admin.",
      };
    case "workspace_required":
      return {
        title: "Workspace context is missing",
        message: "SpryBoard could not determine the active workspace for this meeting launch. Re-open the board from SpryWorkspace and try again.",
      };
    case "invalid_token":
    case "missing_bearer_token":
      return {
        title: "Your sign-in session expired",
        message: "Sign in to SpryBoard again, then retry starting the meeting.",
      };
    default:
      if (status === 401) {
        return {
          title: "Your sign-in session expired",
          message: "Sign in to SpryBoard again, then retry starting the meeting.",
        };
      }

      return {
        title: "Unable to start SpryMeeting",
        message: backendMessage || `${sourceLabel} could not start the linked meeting. Please try again in a moment.`,
      };
  }
};

export const setMeetingLaunchTokenWindowName = (targetWindow: Window, token: string) => {
  try {
    targetWindow.name = `${WINDOW_NAME_PREFIX}${token}`;
  } catch (error) {
    console.warn("[meetingLaunch] Failed to set SpryMeeting launch token on window:", error);
  }
};

export const registerLaunchTokenResponder = ({
  childWindow,
  childOrigin,
  token,
}: RegisterLaunchTokenResponderOptions): (() => void) => {
  let timeoutId: number | null = null;
  let cleanedUp = false;

  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    window.removeEventListener("message", handleMessage);
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  };

  const handleMessage = (event: MessageEvent) => {
    if (event.source !== childWindow || event.origin !== childOrigin) {
      return;
    }

    const type = typeof event.data?.type === "string" ? event.data.type : null;
    if (type !== REQUEST_TYPE) {
      return;
    }

    childWindow.postMessage({ type: RESPONSE_TYPE, token }, childOrigin);
    cleanup();
  };

  window.addEventListener("message", handleMessage);
  timeoutId = window.setTimeout(cleanup, HANDLER_TTL_MS);

  return cleanup;
};
